import { apiClient, handleApiResponse } from './client'
import type { ApiResponse } from '@/types'
import { authService } from '@/api/auth'

export interface CohortCondition {
  type: string
  step_id?: string
  experiment_id?: string
  variant?: string
  value?: string
  operator?: string
  event_name?: string
  pattern?: string
}

export interface CohortDefinition {
  cohort_id: string
  store_id: string
  name: string
  description: string
  conditions: CohortCondition[]
  match_scope: 'session'
  status: 'active' | 'archived'
  created_at: string
  updated_at: string
}

export interface CohortSummary {
  cohort_id: string
  name: string
  sessions: number
  unique_visitors: number
  conversions: number
  revenue: number
  conversion_rate: number
  avg_revenue_per_session: number
  step_hits: Record<string, number>
}

export interface CohortPairwise {
  cohort_id: string
  name: string
  sessions: number
  conversions: number
  conversion_rate: number
  revenue: number
  avg_revenue_per_session: number
  lift_percentage: number
  lift_absolute: number
  z_score: number
  p_value: number
  is_significant: boolean
  confidence_level: number
  direction: string
  step_hits: Record<string, number>
}

export interface CohortComparisonResult {
  store_id: string
  window_days: number
  baseline: CohortSummary
  pairwise: Record<string, CohortPairwise>
  bonferroni_alpha: number
  num_comparisons: number
  winner: string | null
  generated_at: string
}

class CohortService {
  async listCohorts(status: string = 'active'): Promise<CohortDefinition[]> {
    const storeId = authService.getCurrentStoreId()
    const response = await apiClient.get<ApiResponse<{ cohorts: CohortDefinition[] }>>(
      '/api/cohorts',
      { store_id: storeId, status }
    )
    return (response as any).cohorts || handleApiResponse(response).cohorts
  }

  async getCohort(cohortId: string): Promise<CohortDefinition> {
    const response = await apiClient.get<CohortDefinition>(`/api/cohorts/${cohortId}`)
    return response
  }

  async createCohort(data: {
    name: string
    description?: string
    conditions: CohortCondition[]
  }): Promise<CohortDefinition> {
    const storeId = authService.getCurrentStoreId()
    const response = await apiClient.post<{ message: string; cohort: CohortDefinition }>(
      '/api/cohorts',
      { ...data, store_id: storeId }
    )
    return response.cohort
  }

  async updateCohort(cohortId: string, data: Partial<{
    name: string
    description: string
    conditions: CohortCondition[]
  }>): Promise<CohortDefinition> {
    const response = await apiClient.put<{ message: string; cohort: CohortDefinition }>(
      `/api/cohorts/${cohortId}`,
      data
    )
    return response.cohort
  }

  async archiveCohort(cohortId: string): Promise<void> {
    await apiClient.delete(`/api/cohorts/${cohortId}`)
  }

  async compareCohorts(
    baselineCohortId: string,
    compareCohortIds: string[],
    windowDays: number = 7
  ): Promise<CohortComparisonResult> {
    const storeId = authService.getCurrentStoreId()
    const response = await apiClient.post<ApiResponse<CohortComparisonResult>>(
      '/api/cohorts/compare',
      {
        store_id: storeId,
        baseline_cohort_id: baselineCohortId,
        compare_cohort_ids: compareCohortIds,
        window: windowDays
      }
    )
    return handleApiResponse(response)
  }
}

export const cohortService = new CohortService()
